import { useSWRInfinite, SWRInfiniteConfigInterface } from 'swr';
import useBoundFetch, { BoundFetchResult } from '../useBoundFetch';
import { UseReadOptions, UseReadResult } from './useRead';
import { useCallback, useMemo } from 'react';

export interface UseReadInfiniteOptions<R> extends UseReadOptions<R> {
  /** Options to pass into SWR */
  swrOptions?: Partial<SWRInfiniteConfigInterface>;
}

export interface UseReadInfiniteResult<R>
  extends Omit<UseReadResult<R>, 'refetch' | 'mutate'> {
  /** The number of pages currently fetched or fetching */
  size?: number;
  /** Change the number of pages the hook should retrieve */
  setSize?: (newSize: number) => void;
  /** Refetch the whole list of all pages */
  refetch: () => Promise<R>;
  /** Allows modifying the cached value */
  mutate: (data: R) => Promise<R>;
}

const combineBoundFetchResults = <R>(results: BoundFetchResult<R>[]): R[] =>
  (results || []).reduce((list, res) => {
    list.push(res.data);
    return list;
  }, [] as R[]);

let CONFIG: { environment?: string } = {};

if (typeof window !== 'undefined') {
  CONFIG = (window as any).CONFIG || {};
}

/**
 * The useReadInfinite hook lets you implement an infinite pagination pattern where
 * multiple requests are made to fetch contiguous pages of data. Use the `setSize`
 * returned value to increase the total number of pages fetched, and supply a getUrl
 * function as the first parameter which can utilize the current page count to determine
 * the next URL to fetch a page from.
 *
 * Pages are returned as an array of responses in `data`. In order to flatten the
 * responses into a single array, some post-processing will be required. This is
 * a conscious choice instead of requiring the response data to be shaped as an array
 * and merging them internally, which would limit the uses of this hook unnecessarily.
 *
 * @param getUrl A function which takes two parameters: pageIndex and lastResult. It should return the URL of the next page of data, or null if none exists.
 * @param options Options to alter the way the fetcher and SWR work.
 * @example
 * ```tsx
 * const getUrl = (page: number) => `/some/path?page=${page}`;
 *
 * // ...
 *
 * const {data, loading, setSize, size} = useReadInfinite<ResponseType>(
 *   getUrl,
 *   { transformResponse }
 * );
 *
 * // data will be an array of response data objects, according to
 * // whatever transformResponse returns - if it returns an array,
 * // it will be an array of arrays. If it is an object with other
 * // data, it will be an array of those objects
 *
 * return (
 *   <div>
 *     {data.map(responseData => <Page items={responseData} />)}
 *     <button onClick={() => setSize(size + 1)}>load more</button>
 *   </div>
 * )
 * ```
 */
const useReadInfinite = <R = any>(
  getUrl: (index: number, prevData: BoundFetchResult<R>) => string | null,
  options: UseReadInfiniteOptions<R> = {},
): UseReadInfiniteResult<Array<R>> => {
  const { transformResponse, transformError } = options;
  const boundFetch = useBoundFetch<R>(
    {
      transformResponse,
      transformError,
    },
    options.fetchOptions,
  );

  const { data, mutate: swrMutate, error, ...swrResult } = useSWRInfinite<
    BoundFetchResult<R>
  >(getUrl, boundFetch, options.swrOptions);

  const mutate = useCallback(
    async (data?: R[]) => {
      // unwrap result objects
      const res = await swrMutate(data?.map(r => ({ data: r })));
      return combineBoundFetchResults(res);
    },
    [swrMutate],
  );

  return useMemo(
    () => {
      if(error?.message && CONFIG.environment === 'local') {
        console.error(error.message);
      }

      return {
        ...swrResult,
        data: combineBoundFetchResults(data),
        loading: !(options && options.skip) && (!data || swrResult.isValidating),
        refetch: () => mutate(),
        mutate,
      };
    },
    [swrResult, data, swrResult.isValidating, options, mutate],
  );
};

export default useReadInfinite;
